/**
 * Where a notification target leads outside the desktop (#1169).
 *
 * A target names a desktop app and its window params (payload contract v2,
 * #1089). Under the shell-free routes there is no window manager, so the
 * standalone layout provides this context and `useOpenNotification` turns the
 * target into a route instead:
 *
 * - Cortex → Bug-Reports (the reporter's ticket notifications) → `/tickets`,
 *   with `reportId` when the target names one.
 * - An app that has a shell-free route (`STANDALONE_ROUTES`) → that route,
 *   with the params as query (`/chat?view=routines`).
 * - Anything else → the desktop deep link (`/desktop#/w=…`), but only for a
 *   user who may enter the desktop; for everyone else the target leads
 *   nowhere and the notification is only marked read.
 *
 * Without this provider (the desktop) targets keep opening as windows.
 */
import { createContext, useContext, type ReactNode } from 'react'
import { STANDALONE_ROUTES, searchFromParams } from '@/hooks/useAppHost'
import type { AppId } from '@/data/apps'
import type { ResolvedNotificationTarget } from '@/lib/utils/notificationPayload'
import { deepLinkService } from '@/services/deep-link-service'

/** The standalone route of "Meine Tickets". */
export const TICKETS_ROUTE = '/tickets'

/** The Cortex tab the ticket notifications point at. */
const BUG_REPORTS_TAB = 'bug-reports'

export interface StandaloneNotificationHost {
  /** Client-side navigation to an app-internal path (may carry a query or hash). */
  navigate: (to: string) => void
  /** Whether the user may enter the desktop (`webos`). */
  hasDesktop: boolean
}

const NotificationHostContext = createContext<StandaloneNotificationHost | null>(null)

export function NotificationHostProvider({
  host,
  children,
}: {
  host: StandaloneNotificationHost
  children: ReactNode
}) {
  return <NotificationHostContext.Provider value={host}>{children}</NotificationHostContext.Provider>
}

/** The standalone host, or `null` in the desktop. */
export function useStandaloneNotificationHost(): StandaloneNotificationHost | null {
  return useContext(NotificationHostContext)
}

/** The path a target leads to under the shell-free routes, or `null` when it leads nowhere there. */
export function standaloneTargetPath(target: ResolvedNotificationTarget, hasDesktop: boolean): string | null {
  const params = target.params ?? {}

  if (target.appId === 'cortex' && params.tab === BUG_REPORTS_TAB) {
    const reportId = typeof params.reportId === 'string' && params.reportId ? params.reportId : undefined
    return reportId ? `${TICKETS_ROUTE}?${searchFromParams({ reportId })}` : TICKETS_ROUTE
  }

  const route = STANDALONE_ROUTES[target.appId as AppId]
  if (route) {
    const query = searchFromParams(params)
    return query ? `${route}?${query}` : route
  }

  if (!hasDesktop) return null
  return `/desktop${deepLinkService.generateUrl([{ appId: target.appId as AppId, params }])}`
}
